<template>
  <div>
    <label v-if="label" class="input-label" :class="{ regular: regularLabel }">
      {{ label }}
    </label>
    <div
      class="custom-datetime-picker-container"
      :class="[classContainer, error ? 'invalidated' : '']"
    >
      <i :class="getCalendarIcon" />
      <span>
        <Datepicker
          :id="id"
          ref="datePicker"
          :class="'custom-date'"
          :bootstrap-styling="true"
          :language="es"
          :full-month-name="true"
          :monday-first="true"
          :calendar-class="{ 'custom-datetime-picker': true, right }"
          :format="format"
          :placeholder="placeholder"
          :value="formattedValue"
          :use-utc="useUtc"
          :disabled-dates="disabledDates"
          :disabled="isDisabled"
          :required="required"
          :clear-button="isClearEnabled"
          :open-date="openDate"
          clear-button-icon="fa fa-times prevent-close-dialog"
          @input="updateValue"
          @selected="onSelectedDate"
          @keypress="$emit('keypress', $event)"
          @cleared="cleared"
        >
          <template>
            <input
              :required="required"
              :value="value"
              @input="$emit('input', value)"
              @keypress="$emit('keypress', $event)"
            />
          </template>
        </Datepicker>
      </span>
    </div>
    <Error v-if="error" variant="no-border" :message="customErrorMsg" />
  </div>
</template>

<script>
import moment from 'moment';
import Datepicker from 'vuejs-datepicker';
import { es } from 'vuejs-datepicker/dist/locale';
import Error from '@/components/common/Error';

export default {
  name: 'CustomDatePicker',
  meta: {
    description: 'Componente común de selector de fecha personalizado',
    slots: {
      default: {
        type: 'component',
        valid: [],
      },
    },
  },
  data() {
    return {
      es: es,
    };
  },
  mounted() {
    this.$el
      .querySelectorAll('.custom-datetime-picker-container i')[0]
      .addEventListener('click', () => {
        this.$refs.datePicker.showCalendar();
      });
  },
  computed: {
    disabledMinDate() {
      const disabledDate = { to: null };
      if (this.minDate != null) {
        let newMinDate = new Date(
          this.minDate.getFullYear(),
          this.minDate.getMonth(),
          this.minDate.getDate()
        );

        disabledDate.to = newMinDate;
      }

      return disabledDate;
    },
    disabledMaxDate() {
      const disabledDate = { from: null };
      if (this.maxDate != null) {
        disabledDate.from = new Date(this.maxDate.setHours(23, 59, 59, 59));
      }

      return disabledDate;
    },
    disabledDates() {
      const disabledDateObj = {
        ...this.disabledMinDate,
        ...this.disabledMaxDate,
      };
      // JSF: disable all dates that are not provided in the available date list.
      // if the list is emtpty then ignore.
      if (this.availableDatesInNumber.length > 0) {
        disabledDateObj.customPredictor = (dateStr) => {
          // JSF: ignore the time (hour, min, sec...)
          const dateInNumber = Number(new Date(dateStr).setHours(0, 0, 0, 0));
          // date not found will be disabled.
          const found =
            this.availableDatesInNumber.findIndex(
              (date) => date === dateInNumber
            ) > -1;
          // return true if the date will be disabled.
          // return false if the user can select this date.
          return !found;
        };
      }

      return disabledDateObj;
    },
    availableDatesInNumber() {
      return this.availableDates.map((x) =>
        // JSF: ignore the time (hour, min, sec...)
        Number(new Date(x).setHours(0, 0, 0, 0))
      );
    },
    isDisabled() {
      const disabled =
        this.disabled !== undefined &&
        this.disabled !== null &&
        this.disabled !== false;
      return disabled;
    },
    isClearEnabled() {
      return this.isDisabled ? false : true;
    },
    getCalendarIcon() {
      return this.disabled ? 'fa fa-calendar' : 'far fa-calendar-day';
    },
    formattedValue() {
      return this.value
        ? moment(this.value)
            .locale('es-PR')
            .format('YYYY-MM-DDTHH:mm')
        : null;
    },
  },
  methods: {
    onSelectedDate(value) {
      if (value) {
        value = new Date(value);
        value = new Date(
          value.getFullYear(),
          value.getMonth(),
          value.getDate()
        );

        this.$emit('onSelectedDate', value);
      } else {
        this.$emit('onSelectedDate', null);
      }
    },
    updateValue(value) {
      if (value) {
        value = new Date(value);
        value = new Date(
          value.getFullYear(),
          value.getMonth(),
          value.getDate()
        );

        this.$emit('input', value);
      } else {
        this.$emit('input', null);
      }
    },
    cleared() {
      this.$emit('cleared');
    },
  },
  components: {
    Datepicker,
    Error,
  },
  props: {
    id: {
      type: String,
      default: () => '',
      meta: {
        description: 'Id del Componente CustomDatePicker',
        example: 'dpFecha',
      },
    },
    placeholder: {
      type: String,
      default: '',
      meta: {
        description: 'Texto de referencia para el DatePicker',
        example: 'Texto requerido',
      },
    },
    openDate: {
      type: Date,
      required: false,
      default: () => new Date(),
      meta: {
        description: 'Fecha en la cual se abre el datepcker cuando no tiene valor',
      },
    },
    maxDate: {
      type: Date,
      required: false,
      default: null,
      meta: {
        description: 'Estable un límite máximo para la fecha ',
        example: 'Texto requerido',
      },
    },
    minDate: {
      type: Date,
      required: false,
      default: null,
      meta: {
        description: 'Estable un límite mínimo para la fecha ',
        example: 'Texto requerido',
      },
    },
    format: {
      type: String,
      default: function() {
        return 'dd/MMM/yyyy';
      },
      meta: {
        description: 'Establece el formato de fecha en día, mes y año',
        example: 'Texto requerido',
      },
    },
    value: {
      type: [Date, String],
      default: null,
      meta: {
        description: 'Parámetro de entrada del DatePicker',
        example: 'Texto requerido',
      },
    },
    disabled: {
      type: Boolean,
      default: function() {
        return undefined;
      },
      meta: {
        description: 'Estado deshabilitado',
        example: 'isDisabled',
      },
    },
    availableDates: {
      type: Array,
      default: () => [],
      meta: {
        description:
          'lista de fechas habilitadas - deshabilitara todas las que no esten en el listado.',
        example: '["2022-04-08T00:00:00", "2022-04-22T00:00:00"]',
      },
    },
    label: {
      type: String,
      default: '',
      meta: {
        description: 'Control para mostrar texto',
        example: 'Texto requerido',
      },
    },
    required: {
      type: String,
      default: function() {
        return '';
      },
      meta: {
        description: 'Estado requerido',
        example: 'Texto requerido',
      },
    },
    error: {
      type: String,
      default: '',
      meta: {
        description: 'Estado Error',
        example: 'Texto requerido',
      },
    },
    customErrorMsg: {
      type: String,
      default: function() {
        return this.error;
      },
      meta: {
        description: 'Mensaje de error encontrado',
        example: 'Texto requerido',
      },
    },
    right: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Posicionamiento a la derecha',
        example: 'true',
      },
    },
    classContainer: {
      type: String,
      default: 'width-container',
      meta: {
        description: 'Agrega el selector de fecha a un elemento específico',
        example: 'Texto requerido',
      },
    },
    regularLabel: {
      type: Boolean,
      default: () => false,
      meta: {
        description: 'Control para mostrar texto en regular',
        example: 'true',
      },
    },
    useUtc: {
      type: Boolean,
      default: () => false,
    },
  },
};
</script>

<style scope>
.input-label {
  height: 19px;
  text-align: left;
  font: Bold 16px/21px Lato;
  letter-spacing: 0;
  color: gray !important;
  line-height: 19px;
}
.input-label.regular {
  font-weight: 400;
  color: gray;
}
span.invalid:not(input) {
  color: #e85c5c;
}

.width-container {
  width: 192px !important;
}

.invalidated.custom-datetime-picker-container input[type='text'] {
  background-color: #f7d7da;
  border: 1px solid #ff0018 !important;
}

.custom-datetime-picker-container input[type='text'] {
  background: #fff;
  padding-left: 34px;
  text-align: left;
  font: Regular 16px/24px Lato;
  letter-spacing: 0;
  color: #727272;
  opacity: 1;
}

.custom-datetime-picker-container-grey input[type='text'] {
  background: #f2f2f2;
  border: 1px solid #ced4da;
  border-radius: 4px;
  height: 36px;
  padding: 4px;
  width: 173px;
  text-align: center;
  padding-left: 27px;
}

.custom-datetime-picker-container i {
  color: #ff7058;
  position: absolute;
  margin-top: 11px;
  margin-left: 11px;
  z-index: 10;
}

.custom-datetime-picker-container i.prevent-close-dialog {
  margin-top: 27px;
}

.custom-datetime-picker-container.invalidated i {
  color: #ff5b5c;
}

.custom-datetime-picker-container span.cell.day {
  text-align: center;
  font: Bold 13px/20px Lato;
  letter-spacing: 0;
  color: #11291b;
  opacity: 1;
  line-height: 35px;
}

.custom-datetime-picker-container span.cell.day.selected {
  background: #ff7058;
  color: #fff;
  border-radius: 50%;
}

.custom-datetime-picker-container span.cell.day.disabled {
  color: #d1d0d0;
}

.custom-datetime-picker-container header {
  padding: 10px;
  margin-bottom: 36px;
}

.custom-datetime-picker-container header > span.prev {
  background: #f8f8f8;
  border-radius: 50%;
  margin-top: -7px;
  text-indent: unset;
}

.custom-datetime-picker-container header > span.prev:after {
  border-right: none;
}

.custom-datetime-picker-container header > span.next {
  background: #f8f8f8;
  border-radius: 50%;
  margin-top: -7px;
  text-indent: unset;
}

.custom-datetime-picker-container header > span.next:after {
  border-left: none;
}

.custom-datetime-picker-container header + div {
  padding: 0px 10px 10px 10px;
  margin-top: 1px;
}

.custom-datetime-picker-container .day-header {
  background: #f8f8f8;
  font: Bold 13px/20px Lato;
  letter-spacing: 0;
  color: #11291b;
  opacity: 1;
  margin-bottom: 10px;
  line-height: 35px;
}

.custom-datetime-picker-container .cell {
  font: Bold 13px/20px Lato;
  letter-spacing: 0;
  color: #11291b;
  opacity: 1;
}

.custom-datetime-picker-container .cell.selected {
  font: Bold 13px/35px Lato;
  color: #11291b;
  background: #ff7058;
  color: #fff;
}

.custom-datetime-picker {
  background: #fff;
  position: absolute;
  border-radius: 5px;
  z-index: 1031 !important;
}

.custom-datetime-picker + div {
  padding: 0px 10px 10px 10px;
}

.custom-datetime-picker header .prev + span {
  background: transparent;
  font: Bold 13px/20px Lato;
  letter-spacing: 0;
  color: #11291b;
  opacity: 1;
}

.custom-datetime-picker .cell:not(.blank):not(.disabled).day:hover {
  border: 1px solid #ff7058;
}

.custom-datetime-picker .cell:not(.blank):not(.disabled).month:hover {
  border: 1px solid #ff7058;
}

.custom-datetime-picker .cell:not(.blank):not(.disabled).year:hover {
  border: 1px solid #ff7058;
}
.custom-datetime-picker-container input[type='text']:disabled {
  background: #e9ecef;
}
.right {
  right: 0 !important;
}

.custom-datetime-picker-container
  .input-group
  > .form-control:not(:last-child) {
  border-bottom-right-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.custom-datetime-picker-container .vdp-datepicker__clear-button {
  margin-left: 0px;
}

.custom-datetime-picker-container
  .vdp-datepicker__clear-button
  .input-group-text {
  padding: 0px;
  background: #fff;
  border: none;
  position: absolute;
  top: 6px;
  right: 32px;
}

.custom-datetime-picker-container .vdp-datepicker__clear-button i:before {
  font-size: 15px;
  color: #9d9d9d;
}
.custom-date > .input-group > .form-control {
  border: 1px solid rgba(60, 60, 60, 0.26);
}
</style>
